/**
 * Custom Status Panels for the ICS Grid
 * The Status Bar Panel allows you to add your own components to the grid's Status Bar.
 * Use this when the provided status bar components do not meet your requirements.
 *
 * @docs https://www.ag-grid.com/react-data-grid/component-status-bar/
 */

import { type GridApi } from '@ag-grid-community/core';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

interface TableRowsPanelProps {
    api?: GridApi;
}

export interface ExposedTableRowsAPI {
    setVisibleRows: (value: number) => void;
    setTotalRows: (value: number) => void;
}

/**
 * A custom Status Panel to display the number of visible rows and the total number of rows in a table
 *
 */
const TableRowsPanel = forwardRef((props: TableRowsPanelProps, ref) => {
    const { api } = props;
    const [visibleRows, setVisibleRows] = useState(0);
    const [totalRows, setTotalRows] = useState(0);

    useImperativeHandle(ref, (): ExposedTableRowsAPI => {
        return {
            setVisibleRows: (value: number) => {
                setVisibleRows(value);
            },
            setTotalRows: (value: number) => {
                setTotalRows(value);
            },
        };
    });

    const updateStatusBar = () => {
        if (!api) return;
        const groupLevelState = api.getServerSideGroupLevelState();
        const rowCount = api.getDisplayedRowCount();
        const totalRowCount = groupLevelState
            ? groupLevelState[0].info.totalCount
            : 0;
        setTotalRows(totalRowCount);
        setVisibleRows(rowCount);
    };

    useEffect(() => {
        api?.addEventListener('modelUpdated', updateStatusBar);

        // Remove event listener when destroyed
        return () => {
            if (!api?.isDestroyed()) {
                api?.removeEventListener('modelUpdated', updateStatusBar);
            }
        };
    }, []);

    if (!visibleRows || !totalRows) {
        return <div className="ag-status-name-value" />;
    }

    return (
        <div className="ag-status-name-value">
            <span className="component">Showing&nbsp;</span>
            <span className="ag-status-name-value-value">{visibleRows}</span>
            <span className="component">&nbsp;out of&nbsp;</span>
            <span className="ag-status-name-value-value">{totalRows}</span>
            {visibleRows < totalRows && (
                <span className="component">
                    &nbsp;- Use search, filter and sort to narrow down the
                    results
                </span>
            )}
        </div>
    );
});

// @ts-expect-error - displayName is a static property, but TypeScript doesn't know that
TableRowsPanel.displayName = 'TableRowsPanel';

interface InfiniteScrollPanelProps {
    api?: GridApi;
}

const InfiniteScrollPanel = forwardRef(
    (props: InfiniteScrollPanelProps, ref) => {
        const { api } = props;
        const [loaded, setLoaded] = useState(0);
        const [total, setTotal] = useState(0);
        const panelRef = useRef<HTMLDivElement>(null);

        useImperativeHandle(ref, () => ({}));

        useEffect(() => {
            if (!api) return;

            const update = () => {
                const state = api.getServerSideGroupLevelState();
                const totalCount = state[0]?.info?.totalCount ?? 0;
                let loadedCount = 0;
                api.forEachNode((node) => {
                    if (node.data) loadedCount++;
                });
                setTotal(totalCount);
                setLoaded(loadedCount);
            };

            api.addEventListener('modelUpdated', update);
            return () => {
                if (!api.isDestroyed()) {
                    api.removeEventListener('modelUpdated', update);
                }
            };
        }, [api]);

        useEffect(() => {
            const statusBar =
                panelRef.current?.closest<HTMLElement>('.ag-status-bar');
            if (statusBar) {
                statusBar.style.borderTop =
                    loaded > 0 ? '1px solid var(--ag-border-color)' : '';
            }
        }, [loaded]);

        if (!loaded || !total) {
            return <div ref={panelRef} className="ag-status-name-value" />;
        }

        return (
            <div ref={panelRef} className="ag-status-name-value">
                <span className="component">Showing&nbsp;</span>
                <span className="ag-status-name-value-value">{loaded}</span>
                <span className="component">&nbsp;out of&nbsp;</span>
                <span className="ag-status-name-value-value">{total}</span>
                {loaded < total && (
                    <span className="component">
                        &nbsp;- Scroll down to load more
                    </span>
                )}
            </div>
        );
    },
);

// @ts-expect-error - displayName is a static property, but TypeScript doesn't know that
InfiniteScrollPanel.displayName = 'InfiniteScrollPanel';

export { InfiniteScrollPanel, TableRowsPanel };
